/**
 * The navigation drawer's drag, with no DOM attached: where the finger is,
 * which way it has decided to go, how fast it was moving when it lifted, and
 * which side the panel should settle on.
 *
 * The panel follows the finger 1:1 while it is down, so this is not a
 * gesture recogniser that answers once at the end; it is asked on every
 * move. Keeping it to a handful of pure functions over samples is what lets
 * the component stay a thin layer of listeners and inline styles, and what
 * lets the thresholds be pinned here without a touch event in sight.
 *
 * Deliberately not an edge swipe. The edge is not the page's to have:
 * Android's gesture navigation and iOS, in a tab and in an installed app
 * alike, take a swipe that begins on the bezel as Back before the page hears
 * of it, and nothing in web content can refuse them. A drag that can begin
 * anywhere is one a thumb cannot miss; what keeps it from firing by accident
 * is the axis judgement below and the places NavDrawer refuses to listen in.
 */

/** A touch position and the event's timestamp, in ms. */
export interface Sample {
  x: number;
  y: number;
  t: number;
}

export type Side = 'closed' | 'open';

/**
 * What the drag has decided it is. `pending` until the finger has moved far
 * enough to tell; `none` for a scroll, or for a push on a drawer that is
 * already as shut as it goes, and from then on the touch is not ours.
 */
export type Axis = 'pending' | 'horizontal' | 'none';

export interface Drag {
  readonly from: Side;
  readonly start: Sample;
  /** The recent moves, oldest first; trimmed to the velocity window. */
  readonly samples: readonly Sample[];
  readonly axis: Axis;
}

/** How far, in px, the finger moves before its direction is judged. */
export const SLOP = 8;

/** The speed, in px/ms, at which a release is a throw rather than a rest. */
export const FLICK = 0.4;

/** How far back, in ms, a release looks to read its speed. */
export const WINDOW = 100;

/**
 * How long, in ms, a finger may sit still before lifting and still be
 * throwing. A real touchend repeats the last move's position a few ms later;
 * a still finger repeats it for much longer than this.
 */
export const REST = 100;

export function begin(from: Side, sample: Sample): Drag {
  return { from, start: sample, samples: [sample], axis: 'pending' };
}

/**
 * The next sample. Judges the axis while it is pending: clearly more down
 * than across is a scroll and ends the matter; clearly across, twice as far
 * as down, locks; anything between waits for the next move rather than
 * guessing at eight pixels. Pushing a closed drawer further shut is nothing.
 */
export function move(drag: Drag, sample: Sample): Drag {
  if (drag.axis === 'none') return drag;
  // A sample from before the last one is not the finger. Chromium's devtools
  // touch emulation was seen to deliver a sequence's final move early, with
  // a timestamp of zero and a position the finger had not reached; a real
  // touch cannot arrive out of order, so nothing is lost by refusing it.
  const last = drag.samples.at(-1);
  if (last && sample.t < last.t) return drag;
  const since = sample.t - WINDOW;
  const samples = [...drag.samples.filter((s) => s.t >= since), sample];
  if (drag.axis === 'horizontal') return { ...drag, samples };

  const dx = sample.x - drag.start.x;
  const dy = Math.abs(sample.y - drag.start.y);
  let axis: Axis = 'pending';
  if (dy >= SLOP && dy >= Math.abs(dx)) axis = 'none';
  else if (drag.from === 'closed' && dx <= -SLOP) axis = 'none';
  else if (Math.abs(dx) >= SLOP && dy * 2 < Math.abs(dx)) axis = 'horizontal';
  return { ...drag, samples, axis };
}

/** How far the finger has travelled sideways since it landed. */
export function offset(drag: Drag): number {
  const last = drag.samples.at(-1);
  return last ? last.x - drag.start.x : 0;
}

/**
 * The finger's speed at release, in px/ms, read over the recent moves rather
 * than the last two samples: the lift itself arrives as a repeat of the last
 * position, and reading it as a sample would make every throw look like a
 * rest. A finger that did rest before lifting was not throwing.
 */
export function velocity(drag: Drag, released: number): number {
  const first = drag.samples[0];
  const last = drag.samples.at(-1);
  if (!first || !last || first === last) return 0;
  if (released - last.t > REST) return 0;
  const elapsed = last.t - first.t;
  return elapsed > 0 ? (last.x - first.x) / elapsed : 0;
}

/**
 * How much of the panel is showing, 0 to 1, for a finger `dx` from where it
 * landed. Opening starts from nothing and closing from everything; a panel
 * with no width to measure against rests where it started.
 */
export function progress(from: Side, dx: number, width: number): number {
  if (width <= 0) return from === 'closed' ? 0 : 1;
  const raw = from === 'closed' ? dx / width : 1 + dx / width;
  return Math.min(1, Math.max(0, raw));
}

/**
 * Which side the panel settles on when the finger lifts. A throw decides by
 * its direction however far it got; otherwise the nearer side wins, with a
 * panel exactly half showing counted as open, since that is the side the
 * finger was heading for.
 */
export function settle(showing: number, speed: number): Side {
  if (Math.abs(speed) >= FLICK) return speed > 0 ? 'open' : 'closed';
  return showing >= 0.5 ? 'open' : 'closed';
}
